import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import type { CyFunCategory, MaturityEvaluation } from "@/types/maturity";
import { DOMAINS_META } from "@/utils/maturity-colors";
import { computeDomainScore } from "@/utils/maturity-scoring";

type DomainTableProps = {
  evaluation: MaturityEvaluation;
  categories: CyFunCategory[];
};

function DomainTable({ evaluation, categories }: DomainTableProps) {
  const [expandedDomains, setExpandedDomains] = useState<
    Record<string, boolean>
  >({});

  const scores =
    evaluation.eval_state === "FULL_COMPLETE"
      ? evaluation.full_scores
      : evaluation.preliminary_scores;

  const domainScores = useMemo(() => {
    if (!scores) return {};
    const result: Record<string, number> = {};
    for (const domain of DOMAINS_META) {
      result[domain.id] = computeDomainScore(scores, categories, domain.id);
    }
    return result;
  }, [scores, categories]);

  const fallbackAvg = useMemo(() => {
    const vals = Object.values(domainScores).filter((v) => v > 0);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }, [domainScores]);

  const overallScore =
    evaluation.overall_score !== null && evaluation.overall_score !== undefined
      ? evaluation.overall_score
      : fallbackAvg;

  const toggleDomain = (id: string) => {
    setExpandedDomains((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasScores = evaluation.eval_state !== "NOT_STARTED" && scores;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[480px]">
          <div className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-2.5 bg-slate-900 text-xs font-semibold text-white uppercase tracking-wide">
            <span>Domeniu</span>
            <span className="text-center">Țintă</span>
            <span className="text-center">Scor</span>
            <span className="text-center">Progres</span>
          </div>

          {DOMAINS_META.map((domain) => {
            const domainCats = categories.filter(
              (c) => c.activity_code === domain.id
            );
            const isExpanded = expandedDomains[domain.id];
            const score = domainScores[domain.id];

            return (
              <div key={domain.id}>
                <button
                  onClick={() => toggleDomain(domain.id)}
                  className="w-full grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-3 hover:bg-slate-50 transition-colors items-center text-left"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-1.5 h-8 rounded-full"
                      style={{ backgroundColor: domain.color }}
                    />
                    <div>
                      <div
                        className="font-semibold text-sm"
                        style={{ color: domain.color }}
                      >
                        {domain.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {domainCats.length} categorii
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </div>
                  <div className="text-center text-sm text-slate-600">
                    {evaluation.target_score.toFixed(2)}
                  </div>
                  <div className="text-center font-semibold text-sm">
                    {hasScores ? score.toFixed(2) : "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${hasScores ? Math.min((score / evaluation.target_score) * 100, 100) : 0}%`,
                          background: `linear-gradient(90deg, ${domain.color}, ${domain.pill})`,
                        }}
                      />
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {domainCats.map((cat) => (
                      <div
                        key={cat.code}
                        className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-2 pl-12 text-sm text-slate-600"
                      >
                        <div>
                          <span className="font-mono text-xs text-slate-400 mr-2">
                            {cat.code}
                          </span>
                          <span className="text-xs">
                            {cat.name.split(":")[0]}
                          </span>
                        </div>
                        <div className="text-center text-slate-500">
                          {evaluation.target_score.toFixed(2)}
                        </div>
                        <div className="text-center font-medium">
                          {hasScores && scores[cat.code] !== undefined
                            ? Number(scores[cat.code]).toFixed(2)
                            : "—"}
                        </div>
                        <div className="flex items-center gap-2">
                          {hasScores && scores[cat.code] !== undefined && (
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${Math.min((Number(scores[cat.code]) / evaluation.target_score) * 100, 100)}%`,
                                  background: `linear-gradient(90deg, ${domain.color}, ${domain.pill})`,
                                  opacity: 0.6,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div className="grid grid-cols-[1fr_80px_80px_100px] gap-2 px-4 py-3 bg-slate-900 text-white border-t border-slate-200 font-bold text-sm">
            <span>TOTAL</span>
            <span className="text-center">
              {evaluation.target_score.toFixed(2)}
            </span>
            <span className="text-center">
              {hasScores ? overallScore.toFixed(2) : "—"}
            </span>
            <span />
          </div>

          {evaluation.eval_state === "PRELIM_COMPLETE" && (
            <div className="px-4 py-2 text-xs text-slate-400 italic border-t">
              Scor estimat — evaluare preliminară (max 2.50)
            </div>
          )}
          {evaluation.eval_state === "FULL_COMPLETE" &&
            evaluation.full_finalized_at && (
              <div className="px-4 py-2 text-xs text-slate-400 border-t">
                Evaluare completă —{" "}
                {new Date(evaluation.full_finalized_at).toLocaleDateString(
                  "ro-RO"
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default DomainTable;
